/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Helpers that trim a product hit's `imageGroups` down to what a *tile* renders, before the hits are
 * serialized into the SSR streaming document.
 *
 * IMPORTANT — keep this module dependency-free. It is reached from the client bundle: the
 * `ProductCarousel` component (`product-carousel/index.tsx`) re-exports its `loader` from
 * `./loaders`, which imports these helpers, so the whole chain is bundled for the browser. An
 * earlier version imported `hasEmbeddedExternalUrl` from `@/lib/images/dynamic-image`, which pulls
 * `tailwindcss/defaultTheme` at module load; dragging that into the client graph made Vite
 * re-optimize `tailwindcss`, duplicating React and nulling its hook dispatcher (a
 * `useContext`/`useTranslation` crash in the first tile). The embedded-URL check below is inlined
 * to keep this module pure — do not re-introduce a heavy import here.
 */

/**
 * Detects SFCC-mangled double-URLs produced when catalog image paths contain absolute external URLs
 * (the instance host + version token is prepended to a path that itself is an absolute URL). Mirror
 * of `EMBEDDED_EXTERNAL_URL_REGEX` in `@/lib/images/dynamic-image`; inlined here to avoid importing
 * that (tailwind-loading) module into the client-reachable carousel chain. Keep the two in sync.
 */
const EMBEDDED_EXTERNAL_URL_REGEX = /\/v\d+\/(https?:\/\/.+)/;

/** `true` when `url` is an SFCC double-URL embedding a directly-servable absolute URL. */
const hasEmbeddedExternalUrl = (url: string | undefined): boolean => !!url && EMBEDDED_EXTERNAL_URL_REGEX.test(url);

/**
 * The maximum number of colour swatches a product tile renders — the single source of truth for that
 * cap. `ProductTile` slices its swatch colours to this count and {@link projectTileImageGroups} keeps
 * exactly this many colours reachable, so the two can never drift; a drift would let the tile render a
 * swatch whose image group the projection had already dropped, blanking it out. It lives here, in this
 * pure client-safe module, and the tile imports it — never the reverse — so the projection's
 * dependency graph stays free of the heavy tile component (see the module header).
 */
export const PRODUCT_TILE_MAX_SWATCHES = 3;

/**
 * Cap the images carried by each image group down to only what a product *tile* renders.
 *
 * A tile shows, for its selected colour, the primary display image (`images[0]`) plus one hover
 * image (`images[1]`), and one swatch thumbnail per colour (`images[0]` of each `swatch` group).
 * Every additional camera angle in each group — and rich catalogs carry many per colour — is dead
 * document weight on carousels and recommendations, which never expose the full gallery (that is
 * the PDP, which fetches its own product detail fresh).
 *
 * Safe by construction:
 *  - Colour coverage is preserved — every colour's group is kept, only its `images` array is
 *    sliced — so `getImagesForColor(hit, selectedColour, viewType)` still finds the matching group
 *    and never falls back to a blank image.
 *  - Nothing a tile reads indexes past `images[1]` for display groups or `images[0]` for swatch
 *    groups. `ProductTile` explicitly ignores `showNavigationArrows`, so the `hiResImages.length`
 *    gallery/arrows path never fires on a tile.
 *
 * Kept intentionally generic (`viewType`/`images` only) so it applies to both the
 * `ShopperProducts` and `ShopperSearch` `ImageGroup` shapes without coupling to either.
 */
export const capImageGroupsForTile = <T extends { viewType?: string; images?: unknown[] }>(
    imageGroups: T[] | undefined,
    opts: { displayImages?: number; swatchImages?: number } = {}
): T[] | undefined => {
    if (!imageGroups?.length) return imageGroups;
    const { displayImages = 2, swatchImages = 1 } = opts;
    return imageGroups.map((group) => {
        const cap = group.viewType === 'swatch' ? swatchImages : displayImages;
        const images = group.images;
        return images && images.length > cap ? { ...group, images: images.slice(0, cap) } : group;
    });
};

/** The colour value an image group is scoped to, read from its `color` variation attribute (the
 * same key `findImageGroupBy` matches on). `undefined` for product-level groups with no colour. */
const imageGroupColorValue = (group: {
    variationAttributes?: { id?: string; values?: { value?: string }[] }[];
}): string | undefined => group.variationAttributes?.find((a) => a?.id === 'color')?.values?.[0]?.value;

/** Element shape this projection needs to see on an image group. */
type ProjectableImageGroup = {
    viewType?: string;
    images?: { disBaseLink?: string; link?: string }[];
    variationAttributes?: { id?: string; values?: { value?: string }[] }[];
};

/**
 * The subset of a hit's fields the colour resolution reads. Kept loose (all optional) so it accepts
 * both the `ShopperSearch` and `ShopperProducts`-derived hit shapes without coupling to either.
 */
type TileColorContext = {
    variationAttributes?: { id?: string; values?: { value?: string }[] }[];
    // `c_color` is a runtime custom property not in the OAS `representedProduct` type; `id` is
    // included so the real `{ id: string }` shape structurally matches (avoids weak-type errors).
    representedProduct?: { id?: string; c_color?: string } | null;
    /** Native merchant-selected default variant colour, stamped by `int_urlresolver_scapi`. */
    c_defaultVariantColorValue?: string;
};

/**
 * Prune a hit's `imageGroups` down to only the colours a *tile* can actually surface, then cap the
 * images within each kept group (via {@link capImageGroupsForTile}).
 *
 * A tile renders at most these colours:
 *  - the **represented colour** — the initial/face-out image, resolved with the same precedence the
 *    tile uses (native `c_defaultVariantColorValue`, then `representedProduct.c_color`);
 *  - the **shown swatch colours** — the first `maxSwatches` colour values, which desktop hover can
 *    swap the image to (`ProductTile.handleSwatchHover`); and
 *  - the **null-colour fallback group** — when no colour resolves (e.g. recommendation tiles, whose
 *    converted hits carry neither a represented colour nor `c_defaultVariantColorValue`), the tile
 *    renders `getImagesForColor(hit, null, viewType)`, which selects the first display group that
 *    embeds an external URL, else the first group of that viewType. This mirrors that selection and
 *    keeps that exact group so those tiles render an identical image.
 *
 * Every other colour's display + swatch group is unreachable from the tile (colours past the shown
 * swatches aren't rendered and can't be hovered; the full gallery only exists on the PDP, fetched
 * fresh), so dropping them removes serialized document weight without changing what renders.
 *
 * Safe by construction: the kept colours are exactly the ones the tile can display, resolved from
 * the same fields the tile reads, so `getImagesForColor`/`getDecoratedVariationAttributes` still
 * find every group they look up — no lookup can land on a dropped group and blank out.
 */
export const projectTileImageGroups = <G extends ProjectableImageGroup>(
    imageGroups: G[] | undefined,
    colorContext: TileColorContext,
    opts: { maxSwatches?: number; displayImages?: number; swatchImages?: number } = {}
): G[] | undefined => {
    if (!imageGroups?.length) return imageGroups;
    const { maxSwatches = PRODUCT_TILE_MAX_SWATCHES, displayImages = 2, swatchImages = 1 } = opts;

    const colorValues = colorContext.variationAttributes?.find((a) => a?.id === 'color')?.values ?? [];
    const reachable = new Set(
        [
            ...colorValues.slice(0, maxSwatches).map((v) => v?.value),
            colorContext.c_defaultVariantColorValue ?? colorContext.representedProduct?.c_color,
        ].filter((v): v is string => Boolean(v))
    );

    // Identify, per display viewType, the exact group `getImagesForColor(hit, null, viewType)` would
    // select, and keep it so any null-colour tile (recommendations) renders the same image.
    const nullFallbackGroups = new Set<G>();
    for (const viewType of new Set(imageGroups.map((g) => g.viewType))) {
        if (viewType === 'swatch') continue;
        const groups = imageGroups.filter((g) => g.viewType === viewType);
        const preferred = groups.find((g) =>
            g.images?.some((img) => hasEmbeddedExternalUrl(img?.disBaseLink || img?.link))
        );
        const target = preferred ?? groups[0];
        if (target) nullFallbackGroups.add(target);
    }

    const kept = imageGroups.filter((group) => {
        if (nullFallbackGroups.has(group)) return true;
        const color = imageGroupColorValue(group);
        // Keep colourless product-level groups and any reachable colour.
        return color === undefined || reachable.has(color);
    });

    // Defensive: if the filter somehow removed everything, keep the colour-preserving cap.
    const source = kept.length ? kept : imageGroups;
    return capImageGroupsForTile(source, { displayImages, swatchImages });
};
